import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
    TrashIcon,
    SparklesIcon,
    DocumentTextIcon,
    PhotoIcon,
    GlobeAltIcon,
    VideoCameraIcon,
    CloudArrowUpIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { agentInstanceService } from '../../../services/agentService';
import { getUser } from '../../../lib/storage';
import { clientService } from '../../../services/clientService';
import ConfirmationDialog from '../../../components/ui/ConfirmationDialog';
import { EyeIcon } from 'lucide-react';
import { fileService } from '../../../services/fileService';
import { fetchImageUrl } from '../../../helpers/helper';

type KBType = 'pdf' | 'text' | 'image' | 'url' | 'sitemap' | 'video' | 'xml';

export type KBStatus = 'pending' | 'completed' | 'failed';

enum DisplayKBStatus {
    Pending = "processing",
    Completed = "processed",
    Failed = "error"
}

interface KBItem {
    id: string;
    name: string;
    type: KBType;
    size?: string;
    embeddingStatus: KBStatus;
    remoteId?: string;
    remoteUrl?: string;
    s3ObjectData?: any;
    lastUpdated: string;

    agentInstanceId?: string | null;

    clientId?: string | null;
    clientName?: string | null;
}

const getKBType = (doc: { contentType?: string, sourceType?: string }): KBType => {
    const contentType = doc.contentType || '';

    if (contentType.includes('pdf')) return 'pdf';
    if (contentType.includes('xml')) return 'xml';
    if (contentType.includes('text')) return 'text';
    if (contentType.includes('image')) return 'image';
    if (contentType.includes('video')) return 'video';

    if (doc.sourceType === 'url') return 'url';
    if (doc.sourceType === 'sitemap') return 'sitemap';

    return 'text'; // fallback
};

const TypeBadge: React.FC<{ type: KBType }> = ({ type }) => {
    const map: Record<KBType, { icon: React.ComponentType<any>; label: string }> = {
        pdf: { icon: DocumentTextIcon as any, label: 'PDF' },
        xml: { icon: DocumentTextIcon as any, label: 'XML' },
        text: { icon: DocumentTextIcon as any, label: 'Text' },
        image: { icon: PhotoIcon as any, label: 'Image' },
        video: { icon: VideoCameraIcon as any, label: 'Video' },
        url: { icon: GlobeAltIcon as any, label: 'URL' },
        sitemap: { icon: GlobeAltIcon as any, label: 'Sitemap' },
    };
    const I = map[type]?.icon || DocumentTextIcon;
    const label = map[type]?.label || 'Unknown';
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-agentic-gray-100 text-agentic-text-secondary">
            <I className="h-3.5 w-3.5" /> {label}
        </span>
    );
};

const StatusBadge: React.FC<{ embeddingStatus: KBStatus }> = ({ embeddingStatus }) => (
    <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${embeddingStatus === 'completed'
            ? 'bg-agentic-accent-green/10 text-agentic-accent-green'
            : embeddingStatus === 'pending'
                ? 'bg-agentic-accent-yellow/10 text-agentic-accent-yellow'
                : 'bg-agentic-accent-red/10 text-agentic-accent-red'
            }`}
    >
        {embeddingStatus === "completed" ? DisplayKBStatus.Completed : embeddingStatus === "pending" ? DisplayKBStatus.Pending : DisplayKBStatus.Failed}
    </span>
);

const TrainingBadge: React.FC<{ id: string; agentInstanceId: string | null | undefined; onClick?: () => void }> = ({ id, agentInstanceId, onClick }) => (
    <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${agentInstanceId
            ? 'bg-agentic-accent-green/10 text-agentic-accent-green'
            : 'bg-agentic-accent-red/10 text-agentic-accent-red'
            }`}
        onClick={agentInstanceId ? undefined : onClick}
        style={{ cursor: agentInstanceId ? "unset" : "pointer" }}
    >
        {agentInstanceId ? "trained" : "not trained"}
    </span>
);

const KnowledgeBaseTab: React.FC<{ agent: any }> = ({ agent }) => {
    const [search, setSearch] = useState('');
    const [items, setItems] = useState<KBItem[]>([]);
    const [urlType, setUrlType] = useState<KBType>('url');
    const [urlValue, setUrlValue] = useState('');
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [selectedStatuses, setSelectedStatuses] = useState<KBStatus[]>([]);
    const [stats, setStats] = useState({ total: 0, completed: 0, pending: 0, failed: 0 });

    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [selectedClient, setSelectedClient] = useState<string | null>(null);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingTrainId, setPendingTrainId] = useState<string | null>(null);
    const [retraining, setRetraining] = useState(false);

    const [clients, setClients] = useState([]);

    const fetchClients = async () => {
        try {
            const res: any = await clientService.list();
            setClients(res.records || []);
        } catch (err) {
            console.error("Failed to load clients", err);
        }
    }

    useEffect(() => {
        fetchClients();
    }, []);

    useEffect(() => {
        if (!agent?.id) return;

        fetchDocs(true);
    }, [agent?.id]);

    const searchRef = useRef(search);
    const selectedStatusesRef = useRef(selectedStatuses);
    const itemsLengthRef = useRef(items.length);

    useEffect(() => {
        searchRef.current = search;
    }, [search]);

    useEffect(() => {
        selectedStatusesRef.current = selectedStatuses;
    }, [selectedStatuses]);

    useEffect(() => {
        itemsLengthRef.current = items.length;
    }, [items.length]);

    useEffect(() => {
        let interval: NodeJS.Timeout | undefined;

        if (stats.pending > 0) {
            interval = setInterval(() => {
                fetchDocs(true, {
                    isfetchForPending: true,
                    page: 1,
                    limit: itemsLengthRef.current,
                    search: searchRef.current,
                    embeddingStatus: selectedStatusesRef.current,
                });
            }, 5000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [stats.pending]);

    const pageRef = useRef(page);

    useEffect(() => {
        pageRef.current = page;
    }, [page]);

    const loadingRef = useRef(loading);

    useEffect(() => {
        loadingRef.current = loading;
    }, [loading]);

    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const observerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!observerRef.current || !scrollContainerRef.current || !hasMore) return;

        console.log("Observer attached");

        const observer = new IntersectionObserver(
            (entries) => {
                if (
                    entries[0].isIntersecting &&
                    initialLoadDoneRef.current &&
                    !loadingRef.current &&
                    hasMore
                ) {
                    console.log("🔥 Triggered load more");
                    fetchDocs();
                }
            },
            {
                root: scrollContainerRef.current, // ✅ FIXED
                threshold: 0.1, // better than 1.0
            }
        );

        observer.observe(observerRef.current);

        return () => observer.disconnect();
    }, [hasMore]); // ❗ removed observerRef.current

    const didMountRef = useRef(false);

    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }

        if (!agent?.id) return;

        setPage(1);
        setHasMore(true);

        fetchDocs(true); // 👈 refetch with filters
    }, [selectedStatuses]);

    const updateItem = (id: string, patch: Partial<KBItem>) => {
        setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
    };

    const toggleStatus = (status: KBStatus) => {
        setSelectedStatuses(prev =>
            prev.includes(status)
                ? prev.filter(s => s !== status)
                : [...prev, status]
        );
    };

    function mapServerDocumentsToKBItems(resp: any): KBItem[] {
        if (!resp || !Array.isArray(resp.documents)) return [];

        return resp.documents.map((d: any) => {
            const name = d.originalFilename ?? d.title ?? (d.sourceUri ? d.sourceUri.split('/').pop()?.split('?')[0] : 'unknown');
            const remoteUrl = d.sourceUri ?? null;
            const remoteId = d.id ?? d.key ?? null;
            const clientName = d.clientName;
            const lastUpdated = d.updatedAt ? new Date(d.updatedAt).toISOString().slice(0, 10) : (d.createdAt ? new Date(d.createdAt).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10));
            const type: KBType = getKBType(d);

            return {
                id: d.id,
                name: name,
                type,
                size: d.sizeBytes ? `${(d.sizeBytes / 1024 / 1024).toFixed(1)} MB` : undefined,
                embeddingStatus: d.embeddingStatus === 'completed' ? 'completed' : (d.embeddingStatus === 'failed' ? 'failed' : 'pending'),
                remoteId,
                remoteUrl,
                s3ObjectData: d.s3ObjectData,
                lastUpdated,

                agentInstanceId: d.agentInstanceId || null,

                clientId: d.clientId || null,
                clientName,
            } as KBItem;
        });
    }

    const removeDocument: any = async (documentId: string) => {
        try {
            if (!agent?.id) return;

            await agentInstanceService.deleteKBDocument(
                agent.id,
                documentId
            )
            toast.success('Document deleted');
            await fetchDocs(true);
        } catch (err) {
            if ((err as any)?.name === 'AbortError') return;
            console.error('Failed to delete document for agent', err);
            toast.error('Failed to delete document');
            throw err;
        }
    }

    const trainDocumentForAgentInstance = async (documentId: string) => {
        try {
            if (!agent?.id) return;

            await agentInstanceService.trainDocument(agent?.id, documentId);
            toast.success('Retrain started');
            await fetchDocs(true);
        } catch (err) {
            if ((err as any)?.name === 'AbortError') return;
            console.error('Failed to train document for agent', err);
            toast.error('Failed to start retrain');
            throw err;
        }
    };

    const initialLoadDoneRef = useRef(false);

    const fetchDocs = async (
        reset = false,
        options: {
            isfetchForPending?: boolean,
            page?: number,
            limit?: number,
            search?: string,
            embeddingStatus?: KBStatus[]
        } = {}
    ) => {
        if (loadingRef.current) return;

        try {
            setLoading(true);
            if (reset) {
                initialLoadDoneRef.current = false;
            }

            const currentPage = options.page ?? (reset ? 1 : pageRef.current);

            const filters: any = {
                page: currentPage,
                limit: options.limit ?? (reset ? 35 : 10), // load more on 1st load or scroll, less on filter/search change
                search: options.search ?? search,
                embeddingStatus: options.embeddingStatus ?? (selectedStatuses.length ? selectedStatuses : undefined)
            }

            const resp = await agentInstanceService.listAgentInstanceDocuments(agent.id, filters);

            setStats(resp.stats || { total: 0, completed: 0, pending: 0, failed: 0 });

            const serverItems = mapServerDocumentsToKBItems(resp);

            setItems(prev => reset ? serverItems : [...prev, ...serverItems]);

            setHasMore(currentPage < resp.totalPages);

            if (!options.isfetchForPending) {
                if (reset) {
                    setPage(2);
                } else {
                    setPage(prev => prev + 1);
                }
            }
            initialLoadDoneRef.current = true;
        } catch (err) {
            console.error(err);
            initialLoadDoneRef.current = true;
        } finally {
            setLoading(false);
        }
    };

    const didSearchMountRef = useRef(false);

    useEffect(() => {
        if (!didSearchMountRef.current) {
            didSearchMountRef.current = true;
            return;
        }

        if (!agent?.id) return;
        const controller = new AbortController();

        const timeoutId = setTimeout(() => {
            setPage(1);
            setHasMore(true);

            fetchDocs(true);
        }, 500); // <-- debounce delay (change as needed)

        return () => {
            clearTimeout(timeoutId);  // clear old debounce
            controller.abort();       // cancel previous fetch
        };
    }, [agent?.id, search]);


    function normalizeUploadResponse(resp: any) {
        if (!resp) return [];

        const items = Array.isArray(resp.items) ? resp.items : (Array.isArray(resp.data) ? resp.data : []);

        return items.map((it: any) => ({
            name: it.originalName ?? it.name ?? it.fileName,
            key: it.key ?? it.path ?? it.id,
            url: it.publicUrl ?? it.url ?? it.fileUrl ?? it.presignedUrl,
            contentType: it.contentType ?? it.mimeType,
            bucket: it.bucket,
            etag: it.etag,
            raw: it,
        }));
    }

    const handleUploadFilesSequential = async (fileList: FileList | null) => {
        if (!fileList || fileList.length === 0) return;
        const now = new Date().toISOString().slice(0, 10);
        const files = Array.from(fileList);
        const baseTs = Date.now();

        const optimisticItems: KBItem[] = files.map((f, idx) => {
            let type: KBType = 'pdf';
            if (f.type.startsWith('image/')) type = 'image';
            else if (f.type === 'text/plain') type = 'text';
            else if (f.name.toLowerCase().endsWith('.pdf')) type = 'pdf';

            return {
                id: `up-${baseTs}-${idx}-${Math.random().toString(36).slice(2, 8)}-${f.name}`,
                name: f.name,
                type,
                size: `${(f.size / 1024 / 1024).toFixed(1)} MB`,
                lastUpdated: now,
                embeddingStatus: 'pending',
            };
        });

        setItems(prev => [...optimisticItems, ...prev]);

        const user = getUser();
        const ENV_TENANT_ID: any = user?.tenantId;

        for (let i = 0; i < files.length; i++) {
            const f = files[i];
            const optimistic = optimisticItems[i];

            try {
                const resp = await fileService.upload(f, {});
                const uploaded = normalizeUploadResponse(resp)[0] ?? null;

                const remoteId = uploaded?.key ?? uploaded?.etag ?? uploaded?.name ?? null;
                const remoteUrl = uploaded?.url ?? null;
                const bucket = uploaded?.bucket ?? null;
                const displayUrl = remoteUrl ? remoteUrl.split('?')[0] : null;

                toast.success(resp?.message || "Uploaded successfully!");

                updateItem(optimistic.id, {
                    embeddingStatus: 'pending',
                    remoteId,
                    remoteUrl,
                    name: uploaded?.name ?? optimistic.name,
                });
                // fetchDocs();


                try {
                    const payload = {
                        sourceUri: remoteUrl,
                        s3ObjectData: {
                            key: remoteId,
                            bucket,
                            contentType: uploaded?.contentType
                        },
                        // tenantId: ENV_TENANT_ID,
                        originalFilename: uploaded?.name ?? f.name,
                        contentType: uploaded?.contentType ?? f.type ?? '',
                        clientId: selectedClient,
                    };

                    const id = agent?.id;
                    const docResp = await agentInstanceService.AgentInstanceDocument(id, payload);
                    console.log('Document metadata saved:', docResp);

                } catch (metaErr) {
                    console.error('Failed to send document metadata', metaErr);
                    updateItem(optimistic.id, { embeddingStatus: 'failed' });
                }
            } catch (err) {
                console.error('Upload failed for', f.name, err);
                updateItem(optimistic.id, { embeddingStatus: 'failed' });
            }
        }

        fetchDocs()
    };

    const onDropFiles = (files: FileList | null) => {
        handleUploadFilesSequential(files);
    };

    const addUrl = () => {
        const value = urlValue.trim();
        if (!value) return;
        const now = new Date().toISOString().slice(0, 10);
        setItems((prev) => [{ id: `url-${Date.now()}`, name: value, type: urlType, lastUpdated: now, embeddingStatus: 'pending' }, ...prev]);
        setUrlValue('');
    };

    const handleTrainingClick = (documentId: string, embeddingStatus: KBStatus, agentInstanceId?: string | null) => {
        if (agentInstanceId) return;

        if (embeddingStatus !== "completed") {
            toast.error("Cannot train document for agent as it is not processed in vector db.")
            return
        }

        setPendingTrainId(documentId);
        setConfirmOpen(true);
    };

    const handleConfirmRetrain = async () => {
        if (!pendingTrainId) {
            setConfirmOpen(false);
            return;
        }
        setRetraining(true);
        setConfirmOpen(false);
        try {
            await trainDocumentForAgentInstance(pendingTrainId);
        } catch (e) { } finally {
            setRetraining(false);
            setPendingTrainId(null);
        }
    };

    const handleCancelRetrain = () => {
        setConfirmOpen(false);
        setPendingTrainId(null);
    };

    return (
        <>
            <div className="p-6 space-y-6">
                {/* stats cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-agentic-gray-50 rounded-lg p-4">
                        <div
                            onClick={() => setSelectedStatuses([])}
                            className={`p-4 rounded-lg cursor-pointer ${!selectedStatuses.length
                                ? 'bg-green-100'
                                : 'bg-agentic-gray-50'
                                }`}
                        >
                            <div className="text-2xl font-bold text-agentic-text-primary">{stats.total}</div>
                            <div className="text-sm text-agentic-text-secondary">Total Documents</div>
                        </div>
                    </div>

                    <div className="bg-agentic-gray-50 rounded-lg p-4">
                        <div
                            onClick={() => toggleStatus('completed')}
                            className={`p-4 rounded-lg cursor-pointer ${selectedStatuses.includes('completed')
                                ? 'bg-green-100'
                                : 'bg-agentic-gray-50'
                                }`}
                        >
                            <div className="text-2xl font-bold text-agentic-accent-green">{stats.completed}</div>
                            <div className="text-sm text-agentic-text-secondary">Processed</div>
                        </div>
                    </div>

                    <div className="bg-agentic-gray-50 rounded-lg p-4">
                        <div
                            onClick={() => toggleStatus('pending')}
                            className={`p-4 rounded-lg cursor-pointer ${selectedStatuses.includes('pending')
                                ? 'bg-yellow-100'
                                : 'bg-agentic-gray-50'
                                }`}
                        >
                            <div className="text-2xl font-bold text-agentic-accent-yellow">{stats.pending}</div>
                            <div className="text-sm text-agentic-text-secondary">Processing</div>
                        </div>
                    </div>

                    <div className="bg-agentic-gray-50 rounded-lg p-4">
                        <div
                            onClick={() => toggleStatus('failed')}
                            className={`p-4 rounded-lg cursor-pointer ${selectedStatuses.includes('failed')
                                ? 'bg-red-100'
                                : 'bg-agentic-gray-50'
                                }`}
                        >
                            <div className="text-2xl font-bold text-agentic-accent-red">{stats.failed}</div>
                            <div className="text-sm text-agentic-text-secondary">Failed</div>
                        </div>
                    </div>
                </div>

                {/* Upload / Add URL buttons */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 w-full lg:w-auto">
                        <button onClick={() => setShowUploadModal(true)} className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border hover:bg-gray-50">
                            <CloudArrowUpIcon className="h-4 w-4"
                            /> Upload Document
                        </button>
                        <button onClick={() => setUrlType('url')} className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border hover:bg-gray-50">
                            <GlobeAltIcon className="h-4 w-4" /> Add URL
                        </button>
                        <button onClick={() => setUrlType('sitemap')} className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border hover:bg-gray-50">
                            <GlobeAltIcon className="h-4 w-4" /> Add sitemap.xml
                        </button>
                    </div>
                </div>

                <input ref={fileInputRef} type="file" multiple accept=".pdf,.txt,image/*" hidden onChange={(e) => onDropFiles(e.target.files)} />

                {/* drag & drop area (preserved) */}
                {!(urlType === 'url' || urlType === 'sitemap' || urlType === 'video') && (
                    <div
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => { e.preventDefault(); onDropFiles(e.dataTransfer.files); }}
                        className="rounded-xl border border-dashed p-6 text-center text-sm text-agentic-text-secondary bg-agentic-gray-50"
                    >
                        Drag & drop PDF, TXT, or images here, or <button className="text-agentic-primary-blue underline" onClick={() => fileInputRef.current?.click()}>browse</button>
                    </div>
                )}

                {/* URL input */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    <div className="lg:col-span-3 flex items-center gap-2">
                        <select value={urlType} onChange={(e) => setUrlType(e.target.value as KBType)} className="px-2 py-2 rounded-lg border border-gray-200">
                            <option value="url">Web URL</option>
                            <option value="sitemap">Sitemap.xml</option>
                        </select>
                        <input
                            value={urlValue}
                            onChange={(e) => setUrlValue(e.target.value)}
                            placeholder={urlType === 'sitemap' ? 'https://example.com/sitemap.xml' : 'https://example.com/...'}
                            className="flex-1 px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                        />
                        <button onClick={addUrl} className="px-4 py-2 rounded-lg text-white bg-agentic-primary-blue hover:bg-agentic-primary-blue/90">Add</button>
                    </div>
                </div>

                {/* Documents list */}
                <div className="bg-white border rounded-lg flex flex-col max-h-[600px]">
                    <div className="px-6 py-4 border-b border-agentic-gray-200 flex items-center justify-between gap-3">
                        <h4 className="font-medium text-agentic-text-primary">Documents</h4>
                        <div className="w-full max-w-xs">
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search documents..."
                                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
                            />
                        </div>
                    </div>
                    <div
                        ref={scrollContainerRef}
                        className="divide-y divide-agentic-gray-200 overflow-y-auto flex-1"
                    >
                        {items.map((doc: any, i) => (
                            <div key={doc.id} className="px-6 py-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <TypeBadge type={doc.type} />
                                    <div>
                                        <div className="font-medium text-agentic-text-primary">{doc.name} {doc?.clientName ? `(${doc?.clientName})` : ""}</div>
                                        <div className="text-xs text-agentic-text-secondary flex items-center gap-4">
                                            {doc.size && <span>{doc.size}</span>}
                                            <span>Updated {doc.lastUpdated}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <StatusBadge embeddingStatus={doc.embeddingStatus} />

                                    {doc.embeddingStatus === "completed" && (
                                        <TrainingBadge id={doc.id} agentInstanceId={doc.agentInstanceId} onClick={() => handleTrainingClick(doc.id, doc.embeddingStatus, doc.agentInstanceId)} />
                                    )}

                                    <EyeIcon className="w-5 h-5" style={{ cursor: "pointer" }} onClick={async () => {
                                        let url = doc.remoteUrl;
                                        if (doc.s3ObjectData) {
                                            url = await fetchImageUrl(doc.s3ObjectData);
                                        }
                                        if (url) window.open(url, "_blank")
                                    }} />

                                    {(
                                        (doc.embeddingStatus === "completed" && doc.agentInstanceId) ||
                                        doc.embeddingStatus === "failed"
                                    ) && (
                                            <button
                                                className="text-agentic-text-secondary hover:text-agentic-text-primary"
                                                aria-label="Remove"
                                                onClick={() => removeDocument(doc.id)}
                                            >
                                                <XMarkIcon className="h-5 w-5" />
                                            </button>
                                        )}

                                </div>
                            </div>
                        ))}

                        <div ref={observerRef} className="py-4 text-center text-sm text-gray-400">
                            {loading ? "Loading..." : hasMore ? "Scroll to load more" : "No more data"}
                        </div>
                    </div>
                </div>

                <ConfirmationDialog
                    isOpen={confirmOpen}
                    onClose={handleCancelRetrain}
                    onConfirm={handleConfirmRetrain}
                    title="Confirm Train"
                    message="Are you sure you want to train agent with the selected document?"
                    confirmText="Train"
                    cancelText="Cancel"
                    loading={retraining}
                    variant="info"
                />
            </div>
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.txt,image/*"
                hidden
                onChange={(e) => {
                    if (e.target.files) {
                        const newFiles = Array.from(e.target.files);
                        setSelectedFiles(prev => [...prev, ...newFiles]);
                    }
                }}
            />
            {showUploadModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl w-full max-w-lg p-6 shadow-xl space-y-6">

                        <h2 className="text-xl font-semibold">Upload Documents</h2>

                        {/* Selected Documents List */}
                        <div className="border rounded-lg p-4 bg-gray-50 max-h-52 overflow-auto">
                            {selectedFiles.length === 0 ? (
                                <p className="text-gray-500 text-sm">No documents selected yet.</p>
                            ) : (
                                selectedFiles.map((file, idx) => (
                                    <div key={idx} className="flex items-center justify-between py-1">
                                        <span className="text-sm">{file.name}</span>
                                        <button
                                            onClick={() =>
                                                setSelectedFiles((prev) => prev.filter((_, i) => i !== idx))
                                            }
                                            className="text-red-500 text-xs hover:underline"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Add More Documents */}
                        <button
                            className="px-4 py-2 text-sm rounded-lg border w-full flex items-center justify-center gap-2 hover:bg-gray-50"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <CloudArrowUpIcon className="h-4 w-4" /> Add Documents
                        </button>

                        {/* Select Client */}
                        <div className="space-y-1">
                            <label className="text-sm text-gray-700 font-medium">Select Client</label>
                            <select
                                value={selectedClient ?? ""}
                                onChange={(e) => setSelectedClient(e.target.value)}
                                className="w-full border rounded-lg px-3 py-2"
                            >
                                <option value="">Choose client…</option>
                                {clients.map((c: any) => (
                                    <option key={c.clientId} value={c.clientId}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Submit */}
                        <button
                            disabled={selectedFiles.length === 0 || !selectedClient}
                            onClick={() => {
                                setShowUploadModal(false);
                                handleUploadFilesSequential(selectedFiles as any);

                                setSelectedFiles([]);
                                setSelectedClient(null);
                            }}
                            className={`w-full py-2 rounded-lg text-white ${selectedFiles.length === 0 || !selectedClient
                                ? "bg-gray-400"
                                : "bg-agentic-primary-blue hover:bg-agentic-primary-blue/90"
                                }`}
                        >
                            Submit
                        </button>

                        {/* Close */}
                        <button
                            onClick={() => setShowUploadModal(false)}
                            className="w-full text-center text-sm text-gray-500 hover:underline mt-1"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default KnowledgeBaseTab;
